import { Component, OnInit, OnDestroy, inject, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SuperAdminAuthService, SuperAdminUser } from '../../../core/services/superadmin-auth.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { isAdminLayoutFillRoute } from './admin-layout-fill';
import { PageTitleService } from '../../../core/services/page-title.service';
import { PeSelectComponent } from '../pe-select/pe-select.component';
import { PageHeaderIconComponent } from '../page-header-icon/page-header-icon.component';

interface NavItem {
  label: string;
  route: string;
  icon: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, PageHeaderIconComponent],
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss']
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  private authService = inject(SuperAdminAuthService);
  private router = inject(Router);
  private confirmDialogService = inject(ConfirmDialogService);
  private host = inject(ElementRef<HTMLElement>);
  private pageTitleService = inject(PageTitleService);
  private destroy$ = new Subject<void>();

  pageHeader$ = this.pageTitleService.header$;

  sidebarVisible = true;
  userDropdownVisible = false;
  private ignoreUserDropdownClick = false;
  notificationCount = 0;
  userName = 'Super Admin';
  userEmail = '';
  userInitials = 'SA';
  readonly portalRoleLabel = 'Super Admin';

  /** When true, content pane fills the viewport (no outer scroll). See admin-layout-fill.ts. */
  isContentFillMode = false;

  private readonly supportNavItems: NavItem[] = [
    { label: 'Support Tickets', route: '/super-admin/support', icon: 'support_agent' },
    { label: 'Support Team', route: '/super-admin/support-team', icon: 'groups' },
    { label: 'User Impersonation', route: '/super-admin/impersonate', icon: 'person_search' },
    { label: 'Audit Logs', route: '/super-admin/audit', icon: 'history' },
    { label: 'Reports', route: '/super-admin/reports', icon: 'assessment' },
    { label: 'RBAC', route: '/super-admin/rbac', icon: 'admin_panel_settings' },
    { label: 'IP Controls', route: '/super-admin/ip-controls', icon: 'security' }
  ];

  navSections: NavSection[] = [];

  ngOnInit(): void {
    this.loadUserInfo();
    this.rebuildNavSections();
    this.updateContentFillMode(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(event => this.updateContentFillMode(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateContentFillMode(url: string): void {
    this.isContentFillMode = isAdminLayoutFillRoute(url);
  }

  private rebuildNavSections(): void {
    this.navSections = [
      {
        title: 'Overview',
        items: [
          { label: 'Dashboard', route: '/super-admin/dashboard', icon: 'dashboard' },
          { label: 'Organizations', route: '/super-admin/organizations', icon: 'business' },
          { label: 'Admin Users', route: '/super-admin/admin-users', icon: 'admin_panel_settings' },
          { label: 'Customer Users', route: '/super-admin/users', icon: 'people' },
          { label: 'Analytics', route: '/super-admin/analytics', icon: 'analytics' },
          { label: 'Billing & Revenue', route: '/super-admin/billing', icon: 'payments' },
          { label: 'Credit Monitoring', route: '/super-admin/billing/credit-monitoring', icon: 'account_balance_wallet' }
        ]
      },
      {
        title: 'System',
        items: [
          { label: 'Templates', route: '/super-admin/templates', icon: 'description' },
          { label: 'Approval Queue', route: '/super-admin/templates/approval-queue', icon: 'pending_actions' },
          { label: 'AI Models', route: '/super-admin/ai-models', icon: 'psychology' },
          { label: 'Configuration', route: '/super-admin/config', icon: 'settings' },
          { label: 'Feature Flags', route: '/super-admin/feature-flags', icon: 'flag' },
          { label: 'Monitoring', route: '/super-admin/monitoring', icon: 'monitor_heart' }
        ]
      },
      {
        title: 'Parsing Engine',
        items: [
          { label: 'Dashboard', route: '/parsing-engine/dashboard', icon: 'memory' },
          { label: 'Prompt Editor', route: '/parsing-engine/prompts/new', icon: 'edit_note' },
          { label: 'Validation Rules', route: '/parsing-engine/validation-rules', icon: 'rule' },
          { label: 'Parsing Analytics', route: '/parsing-engine/analytics', icon: 'insights' }
        ]
      },
      {
        title: 'Support',
        items: this.supportNavItems
      }
    ];
  }

  private loadUserInfo(): void {
    // Get user info from auth service if available
    const userInfo: SuperAdminUser | null = this.authService.getCurrentUser();
    if (userInfo) {
      const fullName = `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim();
      this.userName = fullName || userInfo.email || 'Admin';
      this.userEmail = userInfo.email || '';
      this.userInitials = this.getInitials(this.userName);
    }
  }

  private getInitials(name: string): string {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  trackNavSection(_index: number, section: NavSection): string {
    return section.title;
  }

  trackNavItem(_index: number, item: NavItem): string {
    return item.route;
  }

  toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  @HostListener('document:pe-close-app-menus')
  closeAppHeaderMenus(): void {
    this.userDropdownVisible = false;
  }

  toggleUserDropdown(): void {
    PeSelectComponent.closeOpenMenus();
    this.userDropdownVisible = !this.userDropdownVisible;
    if (this.userDropdownVisible) {
      // Skip the same click that opened the menu
      this.ignoreUserDropdownClick = true;
      setTimeout(() => {
        this.ignoreUserDropdownClick = false;
      });
    }
  }

  closeUserDropdown(): void {
    this.userDropdownVisible = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.userDropdownVisible || this.ignoreUserDropdownClick) {
      return;
    }
    const target = event.target as Node | null;
    const menuRoot = this.host.nativeElement.querySelector('.user-menu');
    if (menuRoot && target && menuRoot.contains(target)) {
      return;
    }
    this.closeUserDropdown();
  }

  isNavActive(route: string): boolean {
    const url = (this.router.url || '').split('?')[0].split('#')[0];
    if (route === '/super-admin/billing') {
      return url === route || (url.startsWith(`${route}/`) && !url.startsWith('/super-admin/billing/credit-monitoring'));
    }
    if (route === '/super-admin/templates') {
      return url === route || (url.startsWith(`${route}/`) && !url.startsWith('/super-admin/templates/approval-queue'));
    }
    return url === route || url.startsWith(`${route}/`);
  }

  logout(): void {
    this.confirmDialogService.confirm({
      title: 'Sign out of Super Admin?',
      message: 'You will need a new email verification code to return to the portal.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Stay signed in',
      variant: 'danger'
    }).pipe(filter(Boolean), take(1)).subscribe(() => {
      this.authService.logout();
    });
  }

  navigateToNotifications(): void {
    this.router.navigate(['/super-admin/notifications']);
  }

  navigateToProfile(): void {
    this.closeUserDropdown();
    this.router.navigate(['/super-admin/profile']);
  }
}

